import axios from 'axios'

import store from '@/store'

import {objectToSnakeCase} from './utils'
import {API_BASE} from './api'

import Organization from '../services/organizations'

export const LOGIN = `${API_BASE}login/`

export default {
  getHeaders,
  login,
  logout,
  requireAuthAndPermissions,
  requireNoAuth,
  requireSpringFourIntegration,
  requireDonationsEnabled,
  validateToken,
  getAcceptLanguageHeader,
}

async function getAndSetOrganization() {
  await Organization.api.getMyOrganization().then(async organization => {
    store.dispatch('updateOrganizationDetails', organization)
  })
}

function getAcceptLanguageHeader() {
  const currentLanguage = store.state.currentLanguage
  return {
    'Accept-Language': currentLanguage === 'es_ES' ? 'es-ES,es;q=0.9' : 'en-US,en;q=0.9',
  }
}

function getHeaders() {
  if (!store.state.token) {
    return {...getAcceptLanguageHeader()}
  }
  return {
    Authorization: `Token ${store.state.token}`,
    ...getAcceptLanguageHeader(),
  }
}

function login(email, password) {
  const data = objectToSnakeCase({email, password})
  const options = {
    headers: getAcceptLanguageHeader(),
  }
  const promise = axios.post(LOGIN, data, options)
  return promise
}

/* Perform logout by clearing the Vuex store. */
function logout() {
  store.commit('RESET_STATE')
}

/**
 * @function    requireAuthAndPermissions
 * @description returns a vue-router beforeEnter-style function that checks user auth
 *              status and user permission level (isEmployee, isAdmin, isStaff),
 *              and redirect appropriately.
 *              This wrapper function is used to allow for config arguments for @function requireAuth.
 */
function requireAuthAndPermissions({onlyEmployees = false, onlyStaffOrAdmins = false}) {
  if (!onlyEmployees && !onlyStaffOrAdmins) {
    throw new Error(
      'Vue-Router beforeEnter Auth.requireAuthAndPermissions must be configured with onlyEmployees = true OR onlyStaffAndAdmins = true.',
    )
  }

  /**
   * @function    requireAuth
   * @description vue-router beforeEnter-style function to check user auth
   *              status and redirect appropriately.
   *              next() is returned (as opposed to just invoked) because
   *              its invocation does not actually stop the execution of the function,
   *              so some return is needed.
   */
  return async function requireAuth(to, from, next) {
    await store.dispatch('refreshCurrentUser')

    if (!store.getters.userIsLoggedIn) {
      return next({
        name: 'Login',
        query: {redirect: to.fullPath},
      })
    }

    let {isStaff, isAdmin} = store.state.user
    let isStaffOrAdmin = isStaff || isAdmin

    if (onlyStaffOrAdmins) {
      if (isStaffOrAdmin) {
        return next()
      } else {
        return next({
          name: 'Home',
        })
      }
    }

    if (onlyEmployees) {
      if (isStaffOrAdmin) {
        return next({
          name: 'StaffReports',
        })
      } else {
        return next()
      }
    }
  }
}

/**
 *  Return a vue-router beforeEnter-style function that checks that the current user is NOT logged in
 */
function requireNoAuth() {
  return (to, from, next) => {
    if (store.getters.userIsLoggedIn) {
      return next({
        path: '/',
      })
    }
    return next()
  }
}

/**
 * Return a vue-router beforeEnter-style function that checks if the current organization has donations enabled.
 *
 */
function requireDonationsEnabled() {
  return async (to, from, next) => {
    await getAndSetOrganization()

    if (!store.state.organizationDetails.donationsEnabled) {
      return next({
        name: 'Home',
      })
    } else {
      return next()
    }
  }
}

/**
 * returns a vue-router beforeEnter-style function that checks whether an integration
 * with SpringFour is enabled.
 */
function requireSpringFourIntegration() {
  return async (to, from, next) => {
    await getAndSetOrganization()

    if (!store.state.organizationDetails.springFourEnabled) {
      return next({
        name: 'Home',
      })
    } else {
      return next()
    }
  }
}

/**
 * Check a user's authentication token against the API
 *
 * @param {string} token - The authentication token to check.
 * @returns {Promise} - Promise that resolves to true or false.
 */
async function validateToken(token) {
  const params = {token}
  const options = {
    headers: {
      'Content-Type': 'application/json',
      ...getAcceptLanguageHeader(),
    },
  }
  const promise = axios.post(VALIDATE_TOKEN_ENDPOINT, params, options)
  return promise
    .then(response => response.data)
    .catch(error => {
      // eslint-disable-next-line no-console
      console.log('Validate Token API error')
      throw error
    })
}
